/**
 * main.js
 *
 * Application entry point.
 *
 * Changes from original:
 *  - Removed `import '@khmyznikov/pwa-install'` — the component is now loaded
 *    lazily by usePwaInstall.initInstallPrompt() only after login and only in
 *    browser (non-standalone) contexts, keeping the critical bundle lean.
 */

import HoneybadgerVue from '@honeybadger-io/vue';
import { useOneSignal } from '@onesignal/onesignal-vue3';
import 'framework7/bundle';
import Framework7Vue from 'framework7-vue';
import { createApp } from 'vue';
import App from './App.vue';
import { registerComponents } from './components';
import { axiosInstance } from './httpConfig/config/axios.js';
import i18n from './i18n';
import './css/icons.css';
import { appSurface, isTelegramEnv } from './platform/index.js';
import { startInstallFunnel } from './platform/installFunnel.js';
import { setupPinia } from './plugins/piniaPlugin.js';
import './css/app.css';

// Every API call says where the app runs (Telegram Mini App, installed PWA or
// browser) — the backend records it on the profile fetch for the admin
// "Supplier activity" window. Set once, before the first request.
axiosInstance.defaults.headers.common['X-Poolit-Surface'] = appSurface();

// The install funnel, measured per device: this boot's record starts here so
// an install event that fires early is not missed; it rides the profile fetch
// only, as `X-Poolit-Install-Funnel` (useAuth.fetchUserProfile).
startInstallFunnel();

// ─── OneSignal v16 — SEPARATE-INIT plugin shim ────────────────────────────────
// The shipped `OneSignalVuePlugin.install()` (node_modules/@onesignal/onesignal-vue3/
// dist/index.js:454-457) ALWAYS calls `$OneSignal.init(options)` at app.use() time.
// Calling it with no options rejects with "You need to provide your OneSignal appId."
// (an unhandled promise rejection) and — worse — calling it WITH options would
// inject the SDK script and start init BEFORE our `/sw.js` has registered,
// breaking the single-SW invariant (review Section E.1).
//
// We need the convenience of $OneSignal on globalProperties (the namespace is
// what useOneSignal() also returns) WITHOUT the eager init. So we register a
// SHIM that only binds the namespace. The real init runs from
// usePushNotifications() in App.vue <script setup> AFTER `serviceWorker.ready`.
const OneSignalSeparateInitPlugin = {
  install(app) {
    app.config.globalProperties.$OneSignal = useOneSignal();
  },
};

// ── Global error handlers ─────────────────────────────────────────────────────
window.addEventListener('error', (event) => {
  console.error('[Global Error]', event.error);
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('[Unhandled Rejection]', event.reason);
  event.preventDefault();
});

// ── TMA: service-worker cleanup (non-blocking) ────────────────────────────────
// Detection is centralized in platform/index.js so this branch can never drift
// from the platform classification used by App.vue / PwaAdapter / TmaAdapter.
// See PWA audit MEDIUM-8.
if (isTelegramEnv()) {
  import('./utils/serviceWorkerCleanup.js')
    .then(({ cleanupServiceWorkerForTelegram }) => cleanupServiceWorkerForTelegram())
    .catch((err) => console.warn('[Init] TMA SW cleanup failed:', err));
}

// ── Framework7 + Vue bootstrap ────────────────────────────────────────────────
try {
  const app = createApp(App);

  app.use(Framework7Vue, { theme: 'ios' });
  registerComponents(app);
  setupPinia(app);
  app.use(i18n);
  // Register $OneSignal globally without eager init (see shim comment above).
  // The push composable owns the real init() call after SW-ready in PWA-only
  // contexts; TMA never reaches the gate.
  app.use(OneSignalSeparateInitPlugin);

  if (import.meta.env.VITE_ENABLE_HONEYBADGER === 'true') {
    app.use(HoneybadgerVue, {
      apiKey: 'hbp_FR3YFOrink5lxBK38Jf0sc965RcJbA0yOwW2',
      environment: 'production',
      revision: 'master',
    });
  }

  app.mount('#app');

} catch (error) {
  console.error('[Init] Critical app initialization failed:', error);

  document.body.innerHTML = `
    <div style="
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100vh;
      font-family: system-ui;
      text-align: center;
      padding: 20px;
      background: #f5f5f5;
    ">
      <div style="font-size: 64px; margin-bottom: 24px;">😞</div>
      <h1 style="color: #ef4444; margin-bottom: 16px;">فشل تحميل التطبيق</h1>
      <p style="color: #666; margin-bottom: 24px;">عذراً، حدث خطأ منع التطبيق من العمل</p>
      <button
        onclick="window.location.reload()"
        style="
          background: #8E5BFE;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 16px;
          cursor: pointer;
        "
      >
        إعادة المحاولة
      </button>
    </div>
  `;

  throw error;
}
